import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getAuthEmail, isSignedIn } from '@/lib/baba-auth'
import { fetchEmailUsage, isEmailDisabledError, onEmailDisabled, type EmailUsage } from '@/lib/emailApi'

/**
 * baba Email is gated per account. This provider asks `GET /email/usage`
 * once per app load for a signed-in session (and again when the tab comes
 * back to the foreground), and everything that shows the surface — the rail
 * row, the More-sheet tile, the /email routes — reads `enabled` from here.
 *
 * `enabled` starts from the last answer this account got (localStorage), so
 * the rail row and the More-sheet tile are drawn on first paint instead of
 * sliding in after the round trip and shifting everything under them. An
 * account that has never been answered starts at false, so a slow first
 * answer never flashes the entry. Any email call that comes back 503
 * email_disabled flips it back to false through `onEmailDisabled`.
 */

export type EmailGateStatus = 'idle' | 'loading' | 'ready'

interface EmailGateValue {
  status: EmailGateStatus
  enabled: boolean
  usage: EmailUsage | null
  /** The forward address the server told us, or null until it has. */
  forwardTo: string | null
  refresh: () => void
  /** Fetch usage now and resolve with the answer once it is in state (null on failure). */
  refreshNow: () => Promise<EmailUsage | null>
}

const EmailGateContext = createContext<EmailGateValue | null>(null)

const FALLBACK: EmailGateValue = { status: 'idle', enabled: false, usage: null, forwardTo: null, refresh: () => {}, refreshNow: async () => null }

export function useEmailGate(): EmailGateValue {
  return useContext(EmailGateContext) ?? FALLBACK
}

const ENABLED_CACHE_PREFIX = 'baba.email.enabled.'

/** Last `enabled` answer for the signed-in account. MIRROR: iOS EmailModel.persistedEnabled, Android emailStore. */
function readCachedEnabled(): boolean {
  try {
    const email = getAuthEmail()
    if (!email || typeof localStorage === 'undefined') return false
    return localStorage.getItem(ENABLED_CACHE_PREFIX + email.toLowerCase()) === '1'
  } catch {
    return false
  }
}

function writeCachedEnabled(value: boolean): void {
  try {
    const email = getAuthEmail()
    if (!email || typeof localStorage === 'undefined') return
    localStorage.setItem(ENABLED_CACHE_PREFIX + email.toLowerCase(), value ? '1' : '0')
  } catch {}
}

/**
 * Where a claim token waits: while a signed-out visitor signs in, and from the
 * first moment of a claim link's page load (index.html's inline script moves
 * /email/claim/<token> here and rewrites the URL to CLAIM_PATH before any
 * third-party script can read the path).
 */
export const PENDING_CLAIM_KEY = 'baba.email.pending_claim'
export const CLAIM_PATH = '/email/claim'

export function readPendingClaim(): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    return sessionStorage.getItem(PENDING_CLAIM_KEY)
  } catch {
    return null
  }
}

export function writePendingClaim(token: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(PENDING_CLAIM_KEY, token)
  } catch {}
}

export function clearPendingClaim(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(PENDING_CLAIM_KEY)
  } catch {}
}

export function EmailGateProvider({ children }: { children: React.ReactNode }) {
  const signedIn = isSignedIn()
  const [status, setStatus] = useState<EmailGateStatus>('idle')
  const [usage, setUsage] = useState<EmailUsage | null>(null)
  const [cachedEnabled, setCachedEnabled] = useState<boolean>(() => signedIn && readCachedEnabled())
  const [nonce, setNonce] = useState(0)
  const inFlight = useRef(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    if (!signedIn) {
      setStatus('idle')
      setUsage(null)
      setCachedEnabled(false)
      return
    }
    let cancelled = false
    inFlight.current = true
    setStatus((s) => (s === 'ready' ? s : 'loading'))
    fetchEmailUsage()
      .then((result) => {
        if (cancelled) return
        setUsage(result)
        writeCachedEnabled(result.enabled === true)
        setCachedEnabled(result.enabled === true)
      })
      .catch((e) => {
        if (cancelled) return
        setUsage(null)
        // 503 email_disabled is the server saying no: hide it and remember.
        // A 401 or a network failure says nothing about the feature, so the
        // last answer stands rather than blinking a visible row away.
        if (isEmailDisabledError(e)) {
          writeCachedEnabled(false)
          setCachedEnabled(false)
        }
      })
      .finally(() => {
        inFlight.current = false
        if (!cancelled) setStatus('ready')
      })
    return () => {
      cancelled = true
    }
  }, [signedIn, nonce])

  const refresh = useCallback(() => {
    if (inFlight.current) return
    setNonce((n) => n + 1)
  }, [])

  // A direct fetch outside the effect, for the moment a claim may have just
  // turned the account on: the caller needs the fresh answer, not the cached
  // one, and needs to know when it has landed.
  const refreshNow = useCallback(async (): Promise<EmailUsage | null> => {
    if (!isSignedIn()) return null
    try {
      const result = await fetchEmailUsage()
      setUsage(result)
      writeCachedEnabled(result.enabled === true)
      setCachedEnabled(result.enabled === true)
      setStatus('ready')
      return result
    } catch {
      return null
    }
  }, [])

  // The contract says refetch on foreground: a phone that was granted access
  // while the tab sat in the background should not need a reload.
  useEffect(() => {
    if (!signedIn) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [signedIn, refresh])

  useEffect(() => {
    return onEmailDisabled(() => {
      setUsage((current) => (current && current.enabled ? { ...current, enabled: false } : current))
      writeCachedEnabled(false)
      setCachedEnabled(false)
    })
  }, [])

  // A claim link opened while signed out parks its token and sends the
  // visitor through sign-in; sign-in lands on "/", so this is where the
  // claim resumes. Runs once per session load. The token stays parked: the
  // claim page reads it from there, so it never goes back into the URL where
  // analytics and error reporting would read it.
  const resumed = useRef(false)
  useEffect(() => {
    if (!signedIn || resumed.current) return
    const token = readPendingClaim()
    if (!token) return
    resumed.current = true
    if (!pathname.startsWith(CLAIM_PATH)) navigate(CLAIM_PATH, { replace: true })
  }, [signedIn, pathname, navigate])

  const value = useMemo<EmailGateValue>(
    () => ({
      status,
      // The live answer once it lands; until then, the last one.
      enabled: usage ? usage.enabled === true : cachedEnabled,
      usage,
      forwardTo: usage?.forwardTo ?? null,
      refresh,
      refreshNow,
    }),
    [status, usage, cachedEnabled, refresh, refreshNow],
  )

  return <EmailGateContext.Provider value={value}>{children}</EmailGateContext.Provider>
}
